import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcMutualfundsV1DividendAccrualQueryServiceDividendAccrualGet } from '@interxfi/api-client/generated/hooks/ixc-mutualfunds-v1-dividend-accrual-query-service-dividend-accrual-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type {
  IxcMutualfundsV1DividendAccrualKeys,
  IxcMutualfundsV1DividendAccrualPayment,
  IxcMutualfundsV1DividendAccrualState,
  IxcMutualfundsV1DividendSource,
} from '@interxfi/types/generated/api';
import { TableSectionHeader } from '../../../../../../../../../../../../../components/table/table-section-header';
import {
  DividendAccrualEventDrawer,
  DividendAccrualEventSummary,
} from '../../../../../../../../../../../../../features/mutual-funds/dividend-accrual/dividend-accrual-event';
import { getDividendAccrualStateListItems } from '../../../../../../../../../../../../../features/mutual-funds/dividend-accrual/dividend-accrual-list-items';
import { DividendAccrualPaymentsTable } from '../../../../../../../../../../../../../features/mutual-funds/dividend-accrual/dividend-accrual-payments-table';
import { getDividendAccrualPath } from '../../../../../../../../../../../../../features/mutual-funds/dividend-accrual/dividend-accrual-routing';
import { DividendAccrualStatusPill } from '../../../../../../../../../../../../../features/mutual-funds/dividend-accrual/dividend-accrual-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/mutualfunds/dividend-accrual/_dividend-accrual/$accountId/$assetId/$source/$firstDayOfMonth',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `mutual-funds-dividend-accrual-${params.accountId}-${params.assetId}-${params.source}-${params.firstDayOfMonth}`,
        label: 'Dividend Accrual',
        to: getDividendAccrualPath({
          kind: 'detail',
          keys: { ...params, source: params.source as IxcMutualfundsV1DividendSource },
        }),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Mutual Funds Dividend Accrual'),
  component: DividendAccrualDetail,
});

function getListItems(data: IxcMutualfundsV1DividendAccrualState | undefined) {
  const items = getDividendAccrualStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.balance, items.data.dividends, items.data.dividendDate, items.data.expectedPaymentDate],
        },
      ] as const satisfies ReadonlyListGroup[],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.accountId, items.assetId, items.source, items.firstDayOfMonth],
    },
  };
}

function getMenuItems(request: IxcMutualfundsV1DividendAccrualKeys): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: DividendAccrualEventDrawer,
        props: { request },
      },
    },
    actions: undefined,
  };
}

const EMPTY_PAYMENTS: IxcMutualfundsV1DividendAccrualPayment[] = [];

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load dividend accrual', 'view this dividend accrual');

function DividendAccrualDetail() {
  const { accountId, assetId, source, firstDayOfMonth } = Route.useParams();
  const request = { accountId, assetId, source: source as IxcMutualfundsV1DividendSource, firstDayOfMonth } as const;
  const { data, error, isPending } = useIxcMutualfundsV1DividendAccrualQueryServiceDividendAccrualGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const accrual = data?.dividendAccrual;
  const listItems = useMemo(() => getListItems(accrual), [accrual]);
  const menuItems = useMemo(
    () => getMenuItems({ accountId, assetId, source: source as IxcMutualfundsV1DividendSource, firstDayOfMonth }),
    [accountId, assetId, source, firstDayOfMonth],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <DividendAccrualStatusPill isPending={showPending} size="large" status={accrual?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <DividendAccrualEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <DividendAccrualPaymentsTable
          accountId={accountId}
          data={accrual?.data?.payments ?? EMPTY_PAYMENTS}
          headerActions={<TableSectionHeader title="Payments" />}
          showSkeleton={showPending}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
